import { useCallback, useEffect, useState, type ChangeEvent } from 'react';
import { Check, CircleAlert, Loader2 } from 'lucide-react';
import { Input } from '@marine/ui/input';
import { Label } from '@marine/ui/label';
import { Slider, SliderThumb } from '@marine/ui/slider';
import { AiModelSelect } from '@marine/ui/AiModelSelect';
import type { AIModel } from '@marine/contracts/types/ai.types';
import {
  usePatchPlatformAiFeatureConfig,
  type FeatureSaveStatus,
} from '@/modules/platform/hooks/usePlatformAiFeatureConfigs';
import type { PlatformAiFeatureConfig } from '@/modules/platform/services/aiFeatureConfigsApi';

export interface FeatureConfigSectionProps {
  config:             PlatformAiFeatureConfig;
  models:             AIModel[] | undefined;
  isLoadingModels:    boolean;
  onRefreshModels:    () => void;
  isRefreshingModels: boolean;
  filterVision?:      boolean;   // image-analysis passes true per D-17
  label:              string;    // e.g., "Description generation"
  description:        string;    // help text under the feature label
  hardcodedFallback:  string;    // e.g., "anthropic/claude-sonnet-4.6" — shown as hint
}

/**
 * One feature row in the platform Defaults tab (Phase 39.2).
 *
 * Optimistic local state + 750 ms silent auto-save via
 * `usePatchPlatformAiFeatureConfig`. No explicit save button. Error surfaces
 * inline via the `SaveIndicator` pill; success is silent
 * (`feedback_silent_background_sync.md`). Visual shape mirrors tenant-side
 * AiConfigTab's temperature slider / max-tokens input for consistency
 * (`feedback_ui_patterns.md`).
 */
export function FeatureConfigSection({
  config,
  models,
  isLoadingModels,
  onRefreshModels,
  isRefreshingModels,
  filterVision = false,
  label,
  description,
  hardcodedFallback,
}: FeatureConfigSectionProps) {
  const { save, status, lastSavedAt } = usePatchPlatformAiFeatureConfig(config.feature);

  // Local uncontrolled mirror of the server state — the hook's optimistic
  // cache update keeps the prop fresh too, but local state makes the slider/
  // input feel snappy while the server round-trip resolves.
  const [model, setModel]         = useState<string | null>(config.model);
  const [temp, setTemp]           = useState<number>(config.temperature ?? 0.7);
  const [maxTokens, setMaxTokens] = useState<number>(config.max_tokens ?? 2000);

  // Sync from server if the parent re-fetches (list refresh / copy-to-all).
  useEffect(() => { setModel(config.model); }, [config.model]);
  useEffect(() => { if (config.temperature !== null) setTemp(config.temperature); }, [config.temperature]);
  useEffect(() => { if (config.max_tokens  !== null) setMaxTokens(config.max_tokens); }, [config.max_tokens]);

  const handleModelChange = useCallback((next: string) => {
    // AiModelSelect emits '__none__' for "Use default" — translate to null
    // (research §Q2 UX gotcha). Platform-level semantics: NULL = "fall back
    // to hardcoded default".
    const value = next === '__none__' ? null : next;
    setModel(value);
    save({ model: value });
  }, [save]);

  const handleTempChange = useCallback((values: number[]) => {
    const next = values[0] ?? 0.7;
    setTemp(next);
    save({ temperature: next });
  }, [save]);

  const handleMaxTokensChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    const next = Number(e.target.value);
    if (!Number.isFinite(next) || next < 1 || next > 100000) return; // mirrors backend validator
    setMaxTokens(next);
    save({ max_tokens: next });
  }, [save]);

  return (
    <div className="space-y-4 rounded-lg border border-border p-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="text-sm font-medium">{label}</h3>
          <p className="mt-0.5 text-xs text-muted-foreground">{description}</p>
        </div>
        <SaveIndicator status={status} lastSavedAt={lastSavedAt} />
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <div className="space-y-1.5">
          <AiModelSelect
            label="Model"
            value={model}
            onChange={handleModelChange}
            filterVision={filterVision}
            models={models}
            isLoading={isLoadingModels}
            onRefresh={onRefreshModels}
            isRefreshing={isRefreshingModels}
          />
          <p className="text-[10px] text-muted-foreground">
            Leave blank to fall back to hardcoded default: <code>{hardcodedFallback}</code>
          </p>
        </div>

        <div className="space-y-4">
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor={`${config.feature}-temp`} className="text-xs">Temperature</Label>
              <span className="text-xs text-muted-foreground">{temp.toFixed(2)}</span>
            </div>
            <Slider
              id={`${config.feature}-temp`}
              min={0}
              max={2}
              step={0.05}
              value={[temp]}
              onValueChange={handleTempChange}
            >
              <SliderThumb />
            </Slider>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor={`${config.feature}-tokens`} className="text-xs">Max tokens</Label>
            <Input
              id={`${config.feature}-tokens`}
              type="number"
              min={1}
              max={100000}
              value={maxTokens}
              onChange={handleMaxTokensChange}
              className="h-8 text-xs"
            />
          </div>
        </div>
      </div>
    </div>
  );
}

function SaveIndicator({ status, lastSavedAt }: { status: FeatureSaveStatus; lastSavedAt: number | null }) {
  if (status === 'saving') {
    return (
      <span className="flex items-center gap-1 text-[11px] text-muted-foreground">
        <Loader2 className="h-3 w-3 animate-spin" />
        Saving…
      </span>
    );
  }
  if (status === 'error') {
    return (
      <span className="flex items-center gap-1 text-[11px] text-destructive">
        <CircleAlert className="h-3 w-3" />
        Save failed
      </span>
    );
  }
  if (status === 'saved' && lastSavedAt) {
    return (
      <span className="flex items-center gap-1 text-[11px] text-muted-foreground">
        <Check className="h-3 w-3 text-emerald-500" />
        Saved {formatRelative(Date.now() - lastSavedAt)}
      </span>
    );
  }
  return null;
}

function formatRelative(elapsedMs: number): string {
  const s = Math.floor(elapsedMs / 1000);
  if (s < 5)  return 'just now';
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  return `${Math.floor(m / 60)}h ago`;
}
